// Uptime ping row — one dot per (app, env) showing site health.
//
// The data comes from /api/admin/uptime which HEAD-pings each public
// URL in parallel and reports latency + status. Uptime data is global
// (not env-scoped), so this row is the same regardless of which env
// tab the operator is on.

interface PingResult {
  product: 'hub' | 'persona_builder' | 'persona_studio';
  env: 'dev' | 'staging' | 'prod';
  url: string;
  status: 'up' | 'slow' | 'down';
  httpStatus: number | null;
  latencyMs: number;
  error?: string;
}

const PRODUCT_LABEL: Record<PingResult['product'], string> = {
  hub: 'Hub',
  persona_builder: 'Builder',
  persona_studio: 'Studio',
};

const ENV_LABEL: Record<PingResult['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function UptimeRow({ results }: { results: PingResult[] }) {
  if (!results || results.length === 0) return null;

  const downCount = results.filter((r) => r.status === 'down').length;
  const slowCount = results.filter((r) => r.status === 'slow').length;

  // Group by product so the layout reads left→right per app.
  const byProduct: Record<string, PingResult[]> = {};
  for (const r of results) {
    (byProduct[r.product] ??= []).push(r);
  }

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Uptime (live HEAD check)
        </h3>
        <div className="text-[10px] text-slate-500 font-mono">
          {downCount > 0 && <span className="text-rose-400 font-bold">{downCount} down · </span>}
          {slowCount > 0 && <span className="text-amber-400">{slowCount} slow · </span>}
          <span>{results.length - downCount - slowCount} healthy</span>
        </div>
      </div>
      <div className="p-3 grid grid-cols-3 gap-3">
        {Object.entries(byProduct).map(([product, rows]) => (
          <div key={product} className="bg-slate-950/40 border border-slate-800 rounded p-2">
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              {PRODUCT_LABEL[product as PingResult['product']]}
            </div>
            <div className="space-y-1.5">
              {rows.map((r) => (
                <UptimeRowItem key={`${r.product}:${r.env}`} row={r} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UptimeRowItem({ row }: { row: PingResult }) {
  const dotClass =
    row.status === 'up'
      ? 'bg-emerald-400'
      : row.status === 'slow'
        ? 'bg-amber-400'
        : 'bg-rose-500';
  return (
    <a
      href={row.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-2 text-[11px] hover:bg-slate-900/40 rounded px-1.5 py-1 transition-colors"
      title={row.error ? `${row.url} — ${row.error}` : row.url}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
        <span className="text-slate-300">{ENV_LABEL[row.env]}</span>
      </div>
      <div className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
        {row.status === 'down' ? (
          <span className="text-rose-400">{row.httpStatus ?? 'TIMEOUT'}</span>
        ) : (
          <>{row.latencyMs}ms</>
        )}
      </div>
    </a>
  );
}
